import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ElementRef, Renderer2, ViewChild, inject, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxSummernoteModule } from 'ngx-summernote';
import {
  FormBuilder, FormGroup, FormArray, Validators,
  ReactiveFormsModule, AbstractControl,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBar } from '@angular/material/snack-bar';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { SharedModule } from '../shared.module';
import { MeetingEventService, CalendarInvitePayload } from '../../core/service/meeting-event/meeting-event.service';
import { MailServiceService } from '../../core/service/mail/mail-service.service';
import { LocalStorageService } from '../../core/service/localStorage/local-storage.service';
import { ProfileService } from '../../core/service/profile/profile.service';
import timezonesData from '../../../assets/files/timezones.json';

export interface Attendee {
  id?: string | number;
  name: string;
  email: string;
  avatar?: string;
  role: 'organizer' | 'required' | 'optional';
  status?: 'pending' | 'accepted' | 'declined' | 'tentative';
}

export interface DialogData {
  event?: any;
  mode?: 'create' | 'edit';
}

export type RecurrenceType = 'none' | 'daily' | 'weekly' | 'monthly' | 'custom';

// Build 15-min time slots
const TIME_SLOTS: string[] = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    const hour = h % 12 === 0 ? 12 : h % 12;
    const ampm = h < 12 ? 'am' : 'pm';
    TIME_SLOTS.push(`${hour}:${m.toString().padStart(2, '0')} ${ampm}`);
  }
}

const TIMEZONES = timezonesData;

@Component({
  selector: 'app-create-meeting-event',
  standalone: true,
  imports: [
    CommonModule, SharedModule, MatAutocompleteModule, NgxSummernoteModule, ReactiveFormsModule
  ],
  templateUrl: './create-meeting-event.component.html',
  styleUrl: './create-meeting-event.component.scss',
})
export class CreateMeetingEventComponent implements OnInit, OnDestroy {
  private readonly mailService = inject(MailServiceService);
  private readonly cdr = inject(ChangeDetectorRef);
  @Input() data: DialogData = {};
  @Output() closed = new EventEmitter<any>();
  @Output() saved = new EventEmitter<any>();
  private profileService = inject(ProfileService);
  userDetailsData: any = null; 

  userDetails: any

  @ViewChild('descSummernote', { static: false }) descSummernote: any;

  // compose-box state
  isMinimized = false;
  isExpanded = false;

  // Description editor — same config as MailContentComponent
  descriptionHTML = '';

  descConfig = {
    placeholder: 'Add description, agenda, or notes...',
    tabsize: 2,
    height: 300,
    toolbar: [
      ['style', ['bold', 'italic', 'underline', 'clear']],
      ['font', ['fontname', 'fontsize', 'color']],
      ['para', ['ul', 'ol', 'paragraph']],
      ['insert', ['link', 'picture']],
      ['view', ['codeview']],
      ['table', ['table']],
    ],
    fontNames: [
      'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Georgia',
      'Helvetica', 'Impact', 'Lucida Grande', 'Tahoma', 'Times New Roman',
      'Verdana', 'Roboto', 'Open Sans', 'Lato', 'Montserrat',
    ],
    fontNamesIgnoreCheck: [
      'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Georgia',
      'Helvetica', 'Impact', 'Lucida Grande', 'Tahoma', 'Times New Roman',
      'Verdana', 'Roboto', 'Open Sans', 'Lato', 'Montserrat',
    ],
    disableDragAndDrop: true,
    dialogsInBody: true,
    dialogsFade: false,
    popover: {
      link: [['link', ['linkDialogShow', 'unlink']]],
      image: [
        ['image', ['imageDialogShow', 'imageSize100', 'imageSize50', 'imageSize25']],
        ['float', ['floatLeft', 'floatRight', 'floatNone']],
        ['remove', ['removeMedia']],
      ],
    },
  };

  // form
  eventForm!: FormGroup;
  isSubmitting = false;
  formSubmitted = false;

  // UI state
  recurrenceType: RecurrenceType = 'none';
  showCustomRecurrence = false;
  isAllDay = false;
  isTimezoneLoading = false;
  selectedTz = TIMEZONES[0]; // IST default
  showTzPicker = false;
  tzSearch = '';
  timezones = TIMEZONES;
  filteredTimezones = TIMEZONES;
  timeSlots = TIME_SLOTS;
  weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  separatorKeysCodes = [ENTER, COMMA];
  minDate = new Date();

  // Time picker dropdown state
  showStartTimePicker = false;
  showEndTimePicker = false;
  endTimeSlots: { label: string; value: string; duration: string }[] = [];


  // Add these missing properties
  private _attendeeSearchTimeout: any;
  private attendeesResultSubject = new BehaviorSubject<Attendee[]>([]);
  public attendeesResult$ = this.attendeesResultSubject.asObservable();

  // Point the property your HTML uses to the stream where you are pushing data
  filteredAttendees$: Observable<Attendee[]> = this.attendeesResultSubject.asObservable();

  // File attachment properties
  attachments: File[] = [];
  isDraggingOver = false;

  get attendees(): FormArray { return this.eventForm.get('attendees') as FormArray; }
  get recurrenceGroup(): FormGroup { return this.eventForm.get('recurrence') as FormGroup; }
  get isEditMode(): boolean { return this.data?.mode === 'edit'; }

  private destroy$ = new Subject<void>();
  private localStorageService = inject(LocalStorageService);

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private el: ElementRef,
    private renderer: Renderer2,
    private meetingEventService: MeetingEventService,
  ) {
    // Initialize form immediately to prevent template binding errors
    this.buildForm();
  }

  async ngOnInit(): Promise<void> {
    // Move host element to <body> so no parent CSS transform/z-index can trap it
    this.renderer.appendChild(document.body, this.el.nativeElement);

    this.userDetails = await this.localStorageService.getKeyIndexedDbKey('currentUser');
    this.setUserTimezone();
    this.patchExistingData();
    this.watchStartDateTime();
  }

  ngOnDestroy(): void {
    // Clean up — remove host from body when component is destroyed
    if (this.el.nativeElement.parentNode === document.body) {
      this.renderer.removeChild(document.body, this.el.nativeElement);
    }

    // Clear timeout if it exists
    if (this._attendeeSearchTimeout) {
      clearTimeout(this._attendeeSearchTimeout);
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.attendeesResultSubject.complete();
  }

  onDescriptionChange(content: string): void {
    this.descriptionHTML = content;
    this.eventForm.patchValue({ description: content });
  }

  // ── Compose controls ──────────────────────────────────────────────────────

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    if (this.isMinimized) {
      this.isExpanded = false;
      // Also exit fullscreen if active
      this.renderer.removeClass(this.el.nativeElement, 'fullscreen-host');
    }
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    const host = this.el.nativeElement;
    if (this.isExpanded) {
      this.isMinimized = false;
      this.renderer.addClass(host, 'fullscreen-host');
    } else {
      this.renderer.removeClass(host, 'fullscreen-host');
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeTimePickers();
  }

  onClose(): void { this.closed.emit(null); }

  // ── All-day toggle ────────────────────────────────────────────────────────

  toggleAllDay(): void {
    this.isAllDay = !this.isAllDay;
    this.updateTimeValidators();
  }

  private updateTimeValidators(): void {
    const startTimeControl = this.eventForm.get('startTime');
    const endTimeControl = this.eventForm.get('endTime');

    if (this.isAllDay) {
      // Remove required validators for all-day events
      startTimeControl?.clearValidators();
      endTimeControl?.clearValidators();
    } else {
      // Add required validators for timed events
      startTimeControl?.setValidators([Validators.required]);
      endTimeControl?.setValidators([Validators.required]);
    }

    startTimeControl?.updateValueAndValidity();
    endTimeControl?.updateValueAndValidity();
  }

  setUserTimezone(): void {
    if (this.userDetails?.id) {
      this.isTimezoneLoading = true;
      this.cdr.markForCheck();
      this.getUserDetails(this.userDetails?.id).subscribe({
        next: (userRes: any) => {
          this.userDetailsData = userRes?.data?.[0] || userRes?.data;
          if (this.userDetailsData?.userDetails?.timezone) {
            const userTimezone = this.findTimezoneByAbbrAndOffset(
              this.userDetailsData.userDetails.timezone?.abbr,
              this.userDetailsData.userDetails.timezone?.offset,
              this.userDetailsData.userDetails.timezone?.label,
              this.userDetailsData.userDetails.timezone?.iana
            );
            if (userTimezone) {
              this.selectedTz = userTimezone;
            }
          }
          this.isTimezoneLoading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          console.error('Error fetching user details:', err);
          this.isTimezoneLoading = false;
          this.cdr.markForCheck();
        }
      });
    }
  }

  findTimezoneByAbbrAndOffset(abbr: string, offset: string, label?: string, iana?: string): any {
    if (!abbr || !offset) return null;
    if (label && iana) {
      return TIMEZONES.find(tz => 
        tz.abbr === abbr && 
        tz.offset === offset && 
        tz.label === label && 
        tz.iana === iana
      ) || null;
    }
    
    // Fallback to abbr and offset only (for backward compatibility)
    return TIMEZONES.find(tz => tz.abbr === abbr && tz.offset === offset) || null;
  }

  openTzPicker(): void {
    this.tzSearch = '';
    this.filteredTimezones = TIMEZONES;
    this.showTzPicker = true;
  }

  closeTzPicker(): void { this.showTzPicker = false; }

  filterTimezones(q: string): void {
    const query = q.trim().toLowerCase();
    this.filteredTimezones = query
      ? TIMEZONES.filter(tz =>
        tz.label.toLowerCase().includes(query) ||
        tz.abbr.toLowerCase().includes(query) ||
        tz.offset.includes(query))
      : TIMEZONES;
  }

  selectTz(tz: typeof TIMEZONES[0]): void {
    this.selectedTz = tz;
    this.showTzPicker = false;
  }

  getUserDetails(userId: string | number) {
    return this.profileService.getUserDetailsById(userId);
  }

  // ── Recurrence ────────────────────────────────────────────────────────────

  setRecurrenceType(type: RecurrenceType): void {
    this.recurrenceType = type;
    this.showCustomRecurrence = type === 'custom';
    this.recurrenceGroup.patchValue({ enabled: type !== 'none', pattern: type });
  }

  isDaySelected(i: number): boolean {
    return (this.recurrenceGroup.get('daysOfWeek')?.value as number[]).includes(i);
  }

  toggleDay(i: number): void {
    const days = [...(this.recurrenceGroup.get('daysOfWeek')?.value as number[])];
    const idx = days.indexOf(i);
    idx === -1 ? days.push(i) : days.splice(idx, 1);
    this.recurrenceGroup.patchValue({ daysOfWeek: days });
  }

  // ── Form building ─────────────────────────────────────────────────────────

  private buildForm(): void {
    const now = new Date();
    const inOneHr = new Date(now.getTime() + 60 * 60 * 1000);
    this.eventForm = this.fb.group(
      {
        title: ['', [Validators.required, Validators.maxLength(255)]],
        description: [''],
        onlineMeetingUrl: ['', Validators.required],
        startDate: [now, Validators.required],
        startTime: [this.formatTime(this.roundToNext15(now))],
        endDate: [now, Validators.required],
        endTime: [this.formatTime(this.roundToNext15(inOneHr))],
        attendees: this.fb.array([], this.attendeesValidator),
        recurrence: this.fb.group({
          enabled: [false],
          pattern: ['none'],
          daysOfWeek: [[]],
          endType: ['never'],
          endDate: [null],
          occurrences: [1, [Validators.min(1), Validators.max(999)]],
          interval: [1, [Validators.min(1), Validators.max(99)]],
        }),
        attendeeInput: [''],
      },
      { validators: this.dateRangeValidator }
    );

    // Set initial time validators
    this.updateTimeValidators();

    // Build initial end time slots based on default start time
    this.buildEndTimeSlots(this.eventForm.get('startTime')?.value);

    // Reset form state to prevent initial validation errors
    this.eventForm.markAsUntouched();
    this.eventForm.markAsPristine();
  }

  private patchExistingData(): void {
    if (!this.data?.event) return;
    const e = this.data.event;
    this.eventForm.patchValue({
      title: e.title, description: e.description,
      startDate: e.startDate, startTime: e.startTime,
      endDate: e.endDate, endTime: e.endTime,
    });
    e.attendees?.forEach((a: Attendee) => this.addAttendeeToForm(a));
    if (e.recurrence) {
      this.recurrenceGroup.patchValue(e.recurrence);
      this.recurrenceType = e.recurrence.pattern;
    }
  }

  private watchStartDateTime(): void {
    this.eventForm.get('startDate')?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((sd: Date) => {
        const ed = this.eventForm.get('endDate')?.value;
        if (ed && sd > ed) this.eventForm.patchValue({ endDate: sd });
      });

    this.eventForm.get('startTime')?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((st: string) => {
        if (!st) return;
        // Auto-set end time to start + 1hr
        const [h, m] = st.split(':').map(Number);
        const endDate = new Date();
        endDate.setHours(h + 1, m, 0, 0);
        this.eventForm.patchValue({ endTime: this.formatTime(endDate) }, { emitEvent: false });
        this.buildEndTimeSlots(st);
        this.cdr.markForCheck();
      });
  }


  private dateRangeValidator(g: AbstractControl) {
    const sd = g.get('startDate')?.value as Date;
    const ed = g.get('endDate')?.value as Date;
    const st = g.get('startTime')?.value as string;
    const et = g.get('endTime')?.value as string;
    if (!sd || !ed) return null;
    const s = new Date(sd), e = new Date(ed);
    if (st) { const [h, m] = st.split(':').map(Number); s.setHours(h, m); }
    if (et) { const [h, m] = et.split(':').map(Number); e.setHours(h, m); }
    return s >= e ? { invalidDateRange: true } : null;
  }

  private attendeesValidator(control: AbstractControl) {
    const attendeesArray = control as FormArray;
    return attendeesArray.length === 0 ? { required: true } : null;
  }

  onAttendeeInputChange(value: string) {
    const searchTerm = value?.trim();
    if (!searchTerm || searchTerm.length < 2) {
      this.attendeesResultSubject.next([]);
      return;
    }

    clearTimeout(this._attendeeSearchTimeout);
    this._attendeeSearchTimeout = setTimeout(() => {

      this.mailService.searchMailIdByName(searchTerm).subscribe({
        next: (res: any) => {
          const list = res?.data || [];

          // 2. Map strings into the Attendee object structure
          const mappedAttendees: Attendee[] = list.map((email: string) => ({
            name: email,
            email: email,
            role: 'required',
            status: 'pending',
            avatar: ''
          }));

          this.attendeesResultSubject.next(mappedAttendees);

        },
        error: () => this.attendeesResultSubject.next([])
      });
    }, 300);
  }

  addAttendeeFromInput(): void {
    const v = this.eventForm.get('attendeeInput')?.value?.trim();
    if (!v) return;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      this.selectAttendee({
        name: v.split('@')[0],
        email: v,
        role: 'required',
        status: 'pending'
      });
    } else {
      this.snackBar.open('Please enter a valid email address.', 'Close', { duration: 2000 });
    }
  }

  selectAttendee(a: Attendee): void {
    if (!a || !a.email) return;

    // Duplicate Check
    const isAlreadyAdded = this.attendees.controls.some(
      control => control.get('email')?.value?.toLowerCase() === a.email.toLowerCase()
    );

    if (isAlreadyAdded) {
      this.snackBar.open(`${a.email} is already added`, 'OK', { duration: 2000 });
    } else {
      this.addAttendeeToForm(a);
    }

    // Clear input immediately so user can add the next person
    this.eventForm.get('attendeeInput')?.setValue('', { emitEvent: false });
  }

  addAttendeeToForm(a: Attendee): void {
    this.attendees.push(this.fb.group({
      name: [a.name], email: [a.email], role: [a.role || 'required'],
      status: [a.status || 'pending'], avatar: [a.avatar || ''],
    }));
    // Update validation status when attendees are added
    this.attendees.updateValueAndValidity();
  }

  removeAttendee(i: number): void {
    this.attendees.removeAt(i);
    // Update validation status when attendees are removed
    this.attendees.updateValueAndValidity();
  }

  getInitials(name: string): string {
    return name.split(' ').map(n => n[0] || '').join('').toUpperCase().slice(0, 2) || name.slice(0, 2).toUpperCase();
  }

  getAvatarBg(i: number): string {
    return ['#e85d2f', '#378add', '#3b6d11', '#9b59b6', '#e67e22'][i % 5];
  }

  onSubmit(): void {
    this.formSubmitted = true;

    if (this.eventForm.invalid) {
      this.eventForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const payload = this.buildCalendarInvitePayload();
    this.meetingEventService.sendCalendarInvite(payload).pipe(
      takeUntil(this.destroy$),
      catchError(err => {
        this.isSubmitting = false;
        this.snackBar.open(err?.error?.message || 'Failed to send calendar invite.', 'Close', {
          duration: 4000,
          panelClass: ['snack-error']
        });
        throw err;
      })
    ).subscribe(result => {
      this.isSubmitting = false;
      this.snackBar.open('Calendar invite sent successfully!', 'Close', {
        duration: 3000,
        panelClass: ['snack-success']
      });
      this.saved.emit({ success: true, data: result });
      this.closed.emit({ success: true });
    });
  }


  private buildCalendarInvitePayload(): CalendarInvitePayload {
    const v = this.eventForm.value;
    const attendeesList = v.attendees?.map((attendee: any) => ({
      email: attendee.email,
      name: attendee.name
    })) || [];

    // Format recurrence if enabled
    let recurrenceString = '';
    if (v.recurrence?.enabled && this.recurrenceType !== 'none') {
      if (this.recurrenceType === 'custom') {
        // Custom recurrence with detailed configuration
        const recurrenceObj: any = {
          type: 'custom',
          unit: v.recurrence.pattern || 'daily', // daily, weekly, monthly
          interval: v.recurrence.interval || 1,
          ends: v.recurrence.endType || 'never'
        };

        // Add days array for weekly recurrence (convert to day codes)
        if (recurrenceObj.unit === 'weekly' && v.recurrence.daysOfWeek?.length > 0) {
          const dayMap = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
          recurrenceObj.days = v.recurrence.daysOfWeek.map((dayIndex: number) => dayMap[dayIndex]);
        }

        // Add occurrences if ends after specific count
        if (recurrenceObj.ends === 'after_occurrences') {
          recurrenceObj.occurrences = v.recurrence.occurrences || 1;
        }

        // Add endDate if ends on specific date
        if (recurrenceObj.ends === 'on_date' && v.recurrence.endDate) {
          const endDate = new Date(v.recurrence.endDate);
          recurrenceObj.endDate = endDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
        }

        recurrenceString = JSON.stringify(recurrenceObj);
      } else {
        // Simple recurrence - also send as JSON object with just type
        const simpleRecurrenceObj = {
          type: this.recurrenceType // daily, weekly, monthly
        };
        recurrenceString = JSON.stringify(simpleRecurrenceObj);
      }
    }

    // Create timezone object
    const timezoneInfo = {
      abbr: this.selectedTz.abbr,
      offset: this.selectedTz.offset,
    };

    return {
      name: this.userDetails?.name,
      from: this.userDetails?.email,
      to: attendeesList.map((a: any) => a.email).join(','),
      cc: '', // Add CC logic if needed
      subject: v.title || 'Meeting Invitation',
      startTime: this.formatDateTimeForAPI(v.startDate, v.startTime),
      endTime: this.formatDateTimeForAPI(v.endDate, v.endTime),
      calenderName: v.title || 'Meeting Calendar',
      description: v.description || '',
      organizer: this.userDetails?.name,
      attendees: JSON.stringify(attendeesList),
      url: v.onlineMeetingUrl || '',
      platform: 'ZegoCloud',
      recurrence: recurrenceString,
      timezone: timezoneInfo,
      attachments: this.attachments
    };
  }

  // ── Time Picker ───────────────────────────────────────────────────────────

  // Converts "12:00 pm" display format → "HH:mm" 24hr for form value
  convertDisplayTo24(display: string): string {
    const [time, ampm] = display.trim().split(' ');
    let [h, m] = time.split(':').map(Number);
    if (ampm === 'pm' && h !== 12) h += 12;
    if (ampm === 'am' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  // Converts HH:mm (24hr) → "12:00 pm" display label
  formatTimeDisplay(value: string): string {
    if (!value) return '';
    const [h, m] = value.split(':').map(Number);
    const ampm = h < 12 ? 'am' : 'pm';
    const hour = h % 12 === 0 ? 12 : h % 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
  }

  // Builds end time slot list starting from startTime, with duration labels
  buildEndTimeSlots(startTimeValue: string): void {
    if (!startTimeValue) { this.endTimeSlots = []; return; }
    const [sh, sm] = startTimeValue.split(':').map(Number);
    const startMins = sh * 60 + sm;
    const slots: { label: string; value: string; duration: string }[] = [];

    for (let i = 1; i <= 96; i++) { // up to 24hrs in 15-min steps
      const totalMins = startMins + i * 15;
      if (totalMins >= 24 * 60) break;
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      const ampm = h < 12 ? 'am' : 'pm';
      const displayH = h % 12 === 0 ? 12 : h % 12;
      const label = `${displayH}:${m.toString().padStart(2, '0')} ${ampm}`;
      const diffMins = i * 15;
      let duration = '';
      if (diffMins < 60) {
        duration = `${diffMins} mins`;
      } else if (diffMins % 60 === 0) {
        const hrs = diffMins / 60;
        duration = `${hrs} ${hrs === 1 ? 'hr' : 'hrs'}`;
      } else {
        const hrs = diffMins / 60;
        duration = `${hrs} hrs`;
      }
      slots.push({ label, value, duration });
    }
    this.endTimeSlots = slots;
  }

  selectStartTime(value: string): void {
    this.eventForm.patchValue({ startTime: value });
    this.showStartTimePicker = false;
  }

  selectEndTime(value: string): void {
    this.eventForm.patchValue({ endTime: value });
    this.showEndTimePicker = false;
  }

  openStartTimePicker(): void {
    this.showStartTimePicker = true;
    this.showEndTimePicker = false;
  }

  openEndTimePicker(): void {
    const st = this.eventForm.get('startTime')?.value;
    this.buildEndTimeSlots(st);
    this.showEndTimePicker = true;
    this.showStartTimePicker = false;
  }

  closeTimePickers(): void {
    this.showStartTimePicker = false;
    this.showEndTimePicker = false;
  }

  getEndTimeSlots(): string[] { return this.timeSlots; }

  isFieldInvalid(f: string): boolean {
    const ctrl = this.eventForm.get(f);
    if (!ctrl) return false;

    // Don't show errors on initial load - only after user interaction or form submission
    if (!this.formSubmitted && !ctrl.touched && !ctrl.dirty) {
      return false;
    }

    return ctrl.invalid;
  }


  private roundToNext15(d: Date): Date {
    const ms = 15 * 60 * 1000;
    return new Date(Math.ceil(d.getTime() / ms) * ms);
  }

  // Returns HH:mm for native input[type="time"]
  private formatTime(d: Date): string {
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  private formatDateTimeForAPI(date: Date, timeStr: string): string {
    if (!date) return '';
    const d = new Date(date);
    if (!timeStr || this.isAllDay) {
      d.setHours(0, 0, 0, 0);
    } else {
      // Always convert via convertDisplayTo24 in case any am/pm format leaks in,
      // then parse the guaranteed HH:mm 24hr string
      const normalized = timeStr.includes('m') ? this.convertDisplayTo24(timeStr) : timeStr;
      const [h, m] = normalized.split(':').map(Number);
      d.setHours(h, m, 0, 0);
    }

    // Format as local time instead of UTC
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  // ── File Attachment Methods ──────────────────────────────────────────────

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      // Append new files to existing attachments
      this.attachments = [...this.attachments, ...Array.from(input.files)];
    }
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Only hide if leaving the entire compose box
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      this.isDraggingOver = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      // Reuse existing file handling logic
      const fileEvent = {
        target: {
          files: files
        }
      } as any;
      this.onFileSelected(fileEvent);
    }
  }

}